import type { WorkspaceEntity } from "./types";
import { localeTag } from "@/i18n";

export type ActivityClass = "visitor" | "recognized" | "bot";
export type DeviceClassFilter = "desktop" | "mobile" | "tablet";
export type DatePreset = "all" | "today" | "yesterday" | "7d" | "week" | "last_week" | "month" | "last_month" | "custom";

export type LeadFitWeights = {
  company: number;
  confidence: number;
  targetAccount: number;
  companySize: number;
  buyingIntent: number;
};

export const defaultLeadFitWeights: LeadFitWeights = {
  company: 28,
  confidence: 26,
  targetAccount: 20,
  companySize: 14,
  buyingIntent: 22
};
let activeLeadFitWeights: LeadFitWeights = { ...defaultLeadFitWeights };

export function setActiveLeadFitWeights(weights: LeadFitWeights) {
  activeLeadFitWeights = { ...weights };
}

const companyPlaceholders = new Set([
  "consentierter besucher",
  "unbekannt",
  "unbekanntes unternehmen",
  "unbekannte account-aktivität"
]);

const highIntentTerms = /(?:pricing|preise|contact|kontakt|demo|book|termin|conversion|form_reveal|checkout)/i;
const mediumIntentTerms = /(?:solution|lösung|product|produkt|platform|plattform|service|use.?case|anwendungsfall)/i;
const intentCategoryLabels: Record<string, string> = {
  home: "Startseite",
  pricing: "Preise",
  contact: "Kontakt",
  case_study: "Referenzen",
  case_studies: "Referenzen",
  solution: "Lösungen",
  solutions: "Lösungen",
  product: "Produkt",
  integrations: "Integrationen",
  resources: "Ressourcen",
  company: "Unternehmen",
  legal: "Rechtliches",
  security: "Sicherheit"
};

function titleCase(value: string) {
  return value
    .replaceAll(/[-_.]+/g, " ")
    .trim()
    .replace(/(^|\s)\p{L}/gu, (character) => character.toLocaleUpperCase(localeTag()));
}

export function intentCategoryLabel(category?: string, path?: string) {
  const normalized = category?.trim().toLowerCase() ?? "";
  if (normalized && intentCategoryLabels[normalized]) return intentCategoryLabels[normalized];
  if (normalized.startsWith("page.")) return titleCase(normalized.slice(5)) || "Seite";
  if (!normalized || normalized === "other" || normalized === "allgemein" || normalized === "page_view") {
    const pathname = path?.split(/[?#]/)[0]?.replace(/\/+$/, "") ?? "";
    const segment = pathname.split("/").filter(Boolean).filter((part) => !/^(?:de|en)$/i.test(part)).at(-1);
    if (segment) return titleCase(segment);
    if (pathname) return "Startseite";
    return normalized === "other" ? "Seite ohne Pfadlabel (ältere Daten)" : "Startseite";
  }
  return titleCase(normalized) || "Seite";
}

export function activityIntentLabel(entity: WorkspaceEntity) {
  const latestPage = [...(entity.journey ?? [])].reverse().find((event) => event.path);
  return intentCategoryLabel(
    latestPage?.category || entity.intentCategory,
    entity.currentPath || latestPage?.path
  );
}

export function entityHasKnownCompany(entity: WorkspaceEntity) {
  // Trace projections carry an organization only after the shared association
  // policy accepts it. A generated anonymous title and a technical score cannot
  // substitute for that decision.
  if (entity.traceActivity) return Boolean(entity.organizationId);
  // A form declaration/email domain is useful evidence to review, not an
  // independently recognized company. Do not inflate the identified counter.
  if (entity.businessIdentityHint && !entity.organizationId && entity.confidence <= 0) return false;
  const normalizedCompanyName = entity.companyName.trim().toLowerCase();
  return Boolean(
    entity.organizationId
    || entity.domain
    || entity.revealedEmail
    || (
      !companyPlaceholders.has(normalizedCompanyName)
      && !/^(?:historischer\s+)?visitor(?:\s|$)/i.test(normalizedCompanyName)
      && entity.confidence > 0
    )
  );
}

export function classifyActivity(entity: WorkspaceEntity): ActivityClass {
  // Hosting/VPN resolver outcomes are technical or anonymized infrastructure
  // traffic, not evidence of a normal visitor. Keep them fully inspectable in
  // the Bots & Technik filter and Request Explorer without inflating humans.
  if (entity.activityStatus === "bot" || entity.activityStatus === "hosting_or_vpn") return "bot";
  return entityHasKnownCompany(entity) ? "recognized" : "visitor";
}

/** Stable identity used by map clusters so every activity of a selected firm remains visible. */
export function mapActivityIdentityKey(entity: WorkspaceEntity) {
  if (classifyActivity(entity) !== "recognized") return `activity:${entity.activitySourceEntityId || entity.id}`;
  return `company:${entity.organizationId || entity.domain?.trim().toLowerCase() || entity.companyName.trim().toLocaleLowerCase("de")}`;
}

export function normalizedDeviceClass(entity: WorkspaceEntity): DeviceClassFilter | "unknown" {
  const value = entity.deviceClass?.trim().toLowerCase();
  if (value?.includes("tablet") || value?.includes("ipad")) return "tablet";
  if (value?.includes("mobile") || value?.includes("phone")) return "mobile";
  if (value?.includes("desktop") || value?.includes("laptop")) return "desktop";
  return "unknown";
}

export function activityTitle(entity: WorkspaceEntity) {
  return entityHasKnownCompany(entity)
    ? entity.companyName
    : localizedVisitorLabel(entity.visitorLabel || entity.companyName);
}

export function localizedVisitorLabel(value?: string) {
  const label = value?.trim() || (localeTag().startsWith("de") ? "Besucher" : "Visitor");
  if (/^(?:anonymous|unconsented) visit window$/i.test(label)) {
    return localeTag().startsWith("de") ? "Besuchsfenster ohne Einwilligung" : "Visit window without consent";
  }
  const anonymousActivity = label.match(/^(?:anonymous activity|anonyme aktivit[aä]t)(\s+[a-z0-9]+)?$/i);
  if (anonymousActivity) {
    return `${localeTag().startsWith("de") ? "Anonyme Aktivität" : "Anonymous activity"}${anonymousActivity[1] ?? ""}`;
  }
  const automatedRequest = label.match(/^(?:automated request|automatisierter abruf)(\s+[a-z0-9]+)?$/i);
  if (automatedRequest) {
    return `${localeTag().startsWith("de") ? "Automatisierter Abruf" : "Automated request"}${automatedRequest[1] ?? ""}`;
  }
  return localeTag().startsWith("de")
    ? label.replace(/^(historischer\s+)?visitor\b/i, (_, historical: string | undefined) => historical ? "Historischer Besucher" : "Besucher")
    : label.replace(/^(historischer\s+)?besucher\b/i, (_, historical: string | undefined) => historical ? "Historic visitor" : "Visitor");
}

export function activitySubtitle(entity: WorkspaceEntity) {
  const locationLabel = compactActivityLocation(entity);
  if (entityHasKnownCompany(entity)) {
    return [
      localizedVisitorLabel(entity.visitorLabel),
      locationLabel
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return locationLabel;
}

/** Compact list label: optional city plus ISO country code, without evidence wording. */
export function compactActivityLocation(entity: WorkspaceEntity) {
  const location = activityLocation(entity);
  if (location.precision === "unknown") return "";
  const countryCode = location.countryCode.trim().toUpperCase();
  const label = location.label.replace(/\s*\([^)]*\)\s*$/u, "").trim();
  const parts = label.split(",").map((part) => part.trim()).filter(Boolean);
  const first = parts[0] ?? "";
  const normalized = (value: string) => value.normalize("NFKD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("de");
  const countryLabels = new Set([
    countryCode,
    countryCode ? new Intl.DisplayNames(["de"], { type: "region" }).of(countryCode) ?? "" : "",
    countryCode ? new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) ?? "" : ""
  ].map(normalized).filter(Boolean));
  const city = first && !countryLabels.has(normalized(first)) && (
    parts.length > 1 || ["city", "network"].includes(location.precision)
  ) ? first : "";
  return [city, countryCode].filter(Boolean).join(" · ");
}

function normalizedAcquisitionSource(value?: string) {
  const candidate = value?.trim();
  if (!candidate) return "";
  const fixedLabels: Record<string, string> = {
    "google-ads": "Google Ads",
    "google-display-ads": "Google Display Ads",
    "microsoft-ads": "Microsoft Ads",
    "linkedin-ads": "LinkedIn Ads",
    "tiktok-ads": "TikTok Ads",
    "x-ads": "X Ads",
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    tiktok: "TikTok",
    youtube: "YouTube"
  };
  const fixedLabel = fixedLabels[candidate.toLowerCase()];
  if (fixedLabel) return fixedLabel;
  try {
    return new URL(candidate).hostname.replace(/^www\./, "");
  } catch {
    return candidate.replace(/^www\./, "").slice(0, 80);
  }
}

export function acquisitionChannel(value?: string, source?: string) {
  const candidate = value?.trim();
  if (!candidate) return "Direkt";
  const normalized = candidate.toLowerCase();
  const normalizedCategoryLabels: Record<string, string> = {
    direct: "Direkt",
    internal: "Interne Navigation",
    organic_search: "Organic Search",
    paid_search: "Paid Search",
    social: "Social",
    email: "E-Mail",
    referral: "Referral",
    paid_campaign: "Paid Campaign",
    unknown: "Ohne Zuordnung"
  };
  if (normalizedCategoryLabels[normalized]) {
    const label = normalizedCategoryLabels[normalized];
    const sourceLabel = normalizedAcquisitionSource(source);
    return sourceLabel && !["Direkt", "Interne Navigation"].includes(label)
      ? `${label} · ${sourceLabel}`
      : label;
  }
  if (/(?:google|bing|duckduckgo|ecosia|yahoo)\./.test(normalized)) {
    return /(?:gclid|paid|cpc|ppc)/.test(normalized) ? "Paid Search" : "Organic Search";
  }
  if (/(?:facebook|instagram|linkedin|t\.co|twitter|youtube|tiktok)/.test(normalized)) return "Social";
  if (/(?:newsletter|email|e-mail|mailchimp)/.test(normalized)) return "E-Mail";
  if (/(?:cpc|ppc|paid|display|banner|adwords)/.test(normalized)) return "Paid Campaign";
  if (/^(?:direct|\(direct\)|none)$/.test(normalized)) return "Direkt";
  try {
    return `Referral · ${new URL(candidate).hostname.replace(/^www\./, "")}`;
  } catch {
    return candidate.length > 34 ? `${candidate.slice(0, 31)}…` : candidate;
  }
}

function categoryScore(value: string) {
  if (highIntentTerms.test(value)) return 22;
  if (mediumIntentTerms.test(value)) return 12;
  return 5;
}

function clampScore(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function deriveEntityInsights(entity: WorkspaceEntity, leadFitWeights: LeadFitWeights = activeLeadFitWeights) {
  const events = entity.journey ?? [];
  const categories = events.map((event) => intentCategoryLabel(event.category || event.title, event.path)).filter(Boolean);
  const uniqueCategories = [...new Set(categories)];
  const uniquePages = [...new Set(events.map((event) => event.path).filter(Boolean))];
  const engagementMs = Math.max(
    entity.engagementMs ?? 0,
    events.reduce((sum, event) => sum + Math.max(0, event.durationMs ?? 0), 0)
  );
  const interactionDepth = Math.min(24, uniquePages.length * 4 + Math.max(0, events.length - uniquePages.length));
  const intentSignals = uniqueCategories.reduce((sum, category) => sum + categoryScore(category), 0);
  const conversionSignal = events.some((event) => event.type === "conversion" || event.type === "form_reveal") ? 28 : 0;
  const returningVisit = entity.activityOccurrenceId
    ? Boolean(entity.activityReturning)
    : entity.sessions > 1;
  const returnSignal = returningVisit ? Math.min(18, 7 + entity.sessions * 2) : 0;
  const engagementSignal = Math.min(22, engagementMs / 15_000);
  const buyingIntentScore = clampScore(intentSignals + interactionDepth + conversionSignal + returnSignal + engagementSignal);

  const companySignal = (entityHasKnownCompany(entity) ? 1 : 0.14) * leadFitWeights.company;
  const confidenceSignal = (entity.confidenceLabel === "verified" ? 1 : Math.max(0, Math.min(1, entity.confidence))) * leadFitWeights.confidence;
  const targetSignal = (entity.targetAccount ? 1 : 0) * leadFitWeights.targetAccount;
  const sizeFactor = entity.employeeBand === "250+"
    ? 1
    : entity.employeeBand === "50-249"
      ? 0.79
      : entity.employeeBand === "10-49"
        ? 0.5
        : 0.21;
  const sizeSignal = sizeFactor * leadFitWeights.companySize;
  const leadFitScore = clampScore(companySignal + confidenceSignal + targetSignal + sizeSignal + buyingIntentScore / 100 * leadFitWeights.buyingIntent);
  const leadClass = leadFitScore >= 80 && buyingIntentScore >= 55
    ? "A · Sales-ready"
    : leadFitScore >= 62
      ? "B · Hohe Relevanz"
      : leadFitScore >= 42
        ? "C · Nurture"
        : "D · Noch unklar";
  const buyingIntentLabel = buyingIntentScore >= 72
    ? "Sehr hoch"
    : buyingIntentScore >= 52
      ? "Hoch"
      : buyingIntentScore >= 30
        ? "Mittel"
        : "Frühphase";
  const productAffinity = uniqueCategories
    .sort((left, right) => categoryScore(right) - categoryScore(left))[0]
    || activityIntentLabel(entity)
    || "Noch nicht bestimmbar";

  return {
    pageCount: Math.max(uniquePages.length, entity.activityCategoryCount ?? 0, events.length ? 1 : 0),
    eventCount: events.length,
    engagementMs,
    buyingIntentScore,
    buyingIntentLabel,
    leadFitScore,
    leadClass,
    productAffinity,
    acquisition: acquisitionChannel(entity.acquisition, entity.acquisitionSource),
    activityClass: classifyActivity(entity)
  };
}

export function preciseMapLocation(entity: WorkspaceEntity) {
  const location = mapDisplayLocation(entity);
  if (location.lat === undefined || location.lng === undefined) return false;
  if (usesCompanyMapLocation(entity)) return true;
  const infrastructureNetwork = ["provider", "hosting", "vpn", "mobile", "residential"]
    .includes(entity.network.networkClass.toLowerCase());
  // Legacy migration sometimes promoted a hosting-network geolocation to a
  // "verified_site" marker. Keep the activity visible, but never render that
  // infrastructure coordinate as a precise visitor/company location.
  if (entity.dataProvenance === "legacy_v1" && infrastructureNetwork) return false;
  // A company HQ or registered office is master data, never proof that the
  // observed request originated there.
  if (["registered_office", "verified_site"].includes(location.precision)) return false;
  if (location.precision === "city") return !infrastructureNetwork;
  return location.precision === "network"
    && entity.network.networkClass.toLowerCase() === "corporate"
    && (location.radiusKm ?? Number.POSITIVE_INFINITY) <= 25;
}

/**
 * A recognized company's verified site may be shown as a company marker only
 * when it cannot contradict the observed activity country. This keeps a US
 * parent headquarters from being presented for a German visit.
 */
export function usesCompanyMapLocation(entity: WorkspaceEntity) {
  const company = entity.companyLocation;
  if (classifyActivity(entity) !== "recognized" || !company) return false;
  if (company.lat === undefined || company.lng === undefined) return false;
  if (!["registered_office", "verified_site", "city"].includes(company.precision)) return false;
  const activityCountry = activityLocation(entity).countryCode?.trim().toUpperCase();
  const companyCountry = company.countryCode?.trim().toUpperCase();
  if (activityCountry && activityCountry !== companyCountry) return false;
  const addressCountry = entity.address?.countryCode?.trim().toUpperCase();
  return !addressCountry || !companyCountry || addressCountry === companyCountry;
}

export function mapDisplayLocation(entity: WorkspaceEntity) {
  return usesCompanyMapLocation(entity) ? entity.companyLocation! : activityLocation(entity);
}

export function formatEngagementDuration(milliseconds: number, measured = true) {
  if (!measured) return "Nicht messbar";
  if (milliseconds < 1_000) return "< 1 Sek.";
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds} Sek.`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} Std. ${remainder} Min.` : `${hours} Std.`;
}

export function activityLocation(entity: WorkspaceEntity) {
  if (entity.activityOriginLocation && entity.activityOriginLocation.precision !== "unknown") {
    return entity.activityOriginLocation;
  }
  if (classifyActivity(entity) === "recognized" && ["registered_office", "verified_site", "city"].includes(entity.location.precision)) {
    return { label: "Standort der Aktivität nicht bestimmt", countryCode: "", precision: "unknown" as const };
  }
  return entity.location;
}

export function dateRangeForPreset(preset: DatePreset, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isoDate = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
  if (preset === "custom" || preset === "all") return { from: "", to: "" };
  if (preset === "today") return { from: isoDate(startOfToday), to: isoDate(startOfToday) };
  if (preset === "yesterday") {
    const yesterday = new Date(startOfToday);
    yesterday.setDate(yesterday.getDate() - 1);
    return { from: isoDate(yesterday), to: isoDate(yesterday) };
  }
  const weekday = startOfToday.getDay() || 7;
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - weekday + 1);
  if (preset === "week") return { from: isoDate(startOfWeek), to: isoDate(startOfToday) };
  if (preset === "last_week") {
    const from = new Date(startOfWeek);
    from.setDate(from.getDate() - 7);
    const to = new Date(startOfWeek);
    to.setDate(to.getDate() - 1);
    return { from: isoDate(from), to: isoDate(to) };
  }
  if (preset === "7d") {
    return {
      from: isoDate(new Date(startOfToday.getTime() - 6 * 86_400_000)),
      to: isoDate(startOfToday)
    };
  }
  if (preset === "month") {
    return {
      from: isoDate(new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1)),
      to: isoDate(startOfToday)
    };
  }
  const previousMonth = new Date(startOfToday.getFullYear(), startOfToday.getMonth() - 1, 1);
  return {
    from: isoDate(previousMonth),
    to: isoDate(new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 0))
  };
}
